import { PromotionListResponseType } from "@sellar-io/api-v2/types";
import {
  BasketContextActionEnum,
  useBasketContext,
} from "../../../../../hooks";
import { BadgePurple } from "../../../../../components/badge/Badge";
import { ImageWithFallback } from "../../../../../components/image/imageWithFallback";
import {
  calculateItemTotal,
  formatCurrency,
  pluralize,
} from "@sellar-io/common";
import { H2, H6, Icon, SmallButton } from "@sellar-io/components";
import {
  useStorefrontPromotion,
  useSyncPromotionPriceTier,
  useSyncPromotionSupplierId,
} from "./StorefrontPromotionProvider.shared";
import { getPercentageSpendRequirementMet } from "./getPercentageSpendRequirementMet";
import { ReactNode } from "react";
import db from "../../../../../db";
import { isPromotionalOrderItem } from "./isPromotionalOrderItem";
import { useMediaQuery } from "usehooks-ts";
import { Toast } from "../../../../../components/toast/Toast";
import { useToast } from "../../../../../components/toast/useToast.shared";
import { StorefrontPromotionCollapsible } from "./StorefrontPromotionCollapsible";
import { getCanPromotionRewardItemBeClaimed } from "./getCanPromotionRewardItemBeClaimed";
import { sortPromotionRewardItemsBySpendThreshold } from "./sortPromotionRewardItemsBySpendThreshold";

type StorefrontPromotionLayoutProps = {
  priceTierId: number | undefined;
  supplierId: number | undefined;
  supplierLogoUrl: string;
};
export const StorefrontPromotionLayout = ({
  priceTierId,
  supplierId,
  supplierLogoUrl,
}: StorefrontPromotionLayoutProps) => {
  const { toast } = useToast();
  const {
    promotions,
    claimedPromotionRewardItemIds,
    claimPromotionRewardItem,
    unclaimPromotionRewardItem,
    applyEligiblePromotionRewards,
  } = useStorefrontPromotion();
  useSyncPromotionPriceTier({ priceTierId: priceTierId ?? 0 });
  useSyncPromotionSupplierId({ supplierId: supplierId ?? 0 });
  const [{ order: basketOrder }, dispatchBasket] = useBasketContext();

  const handleApplyPromotionsToBasket = (
    claimedPromotionRewardItemIds: number[],
  ) => {
    let newBasketOrder = basketOrder
      ? applyEligiblePromotionRewards({
          order: basketOrder,
          claimedPromotionRewardItemIds,
        })
      : basketOrder;

    const updatedOrder = {
      ...newBasketOrder,
      RetailerId: newBasketOrder?.RetailerId ?? 0,
    };

    dispatchBasket({
      type: BasketContextActionEnum.CHANGE_ORDER,
      order: updatedOrder,
    });

    // This allows us to persist changes between reloads
    db.draftOrders.put(updatedOrder);
  };

  const handleAddToBasket = ({
    promotionRewardItem,
  }: {
    promotionRewardItem: PromotionListRewardItemType;
  }) => {
    const { canBeClaimed: canPromotionRewardItemBeClaimed, message } =
      getCanPromotionRewardItemBeClaimed({
        promotionRewardItemId: promotionRewardItem.id,
        nonPromotionBasketOrderItems,
        claimedPromotionRewardItemIds,
        promotions,
      });

    if (!canPromotionRewardItemBeClaimed) {
      let toastMessage = "Unable to claim this reward";
      switch (message) {
        case "REWARD_RULE_MET": {
          toastMessage = "Only 1 reward can be added from this promotion";
          break;
        }
        case "SPEND_THRSHOLD_NOT_MET": {
          toastMessage = `You need to spend up to ${formatCurrency(promotionRewardItem.spendThreshold)} to claim this reward`;
          break;
        }
        case "MISSING_PROMOTION":
        case "MISSING_PROMOTION_REWARD_ITEM":
        case "PROMOTION_REWARD_ITEM_ALREADY_CLAIMED":
        case "UNKNOWN_ERROR":
      }

      toast({
        placement: "bottom",
        render: () => (
          <Toast icon="ALERT" className="mx-4">
            {toastMessage}
          </Toast>
        ),
      });

      return;
    }

    claimPromotionRewardItem({
      promotionRewardItemIds: [promotionRewardItem.id],
      onComplete: handleApplyPromotionsToBasket,
    });
  };

  const nonPromotionBasketOrderItems =
    basketOrder?.OrderItems?.filter((orderItem) => {
      return !isPromotionalOrderItem(orderItem);
    }) ?? [];

  const basketSubtotal = calculateItemTotal(nonPromotionBasketOrderItems);

  const hasSinglePromotion = promotions.length === 1;

  if (!priceTierId || promotions.length === 0) {
    return null;
  }

  return (
    <div className="mx-2 flex flex-col">
      <StorefrontPromotionCollapsible
        defaultOpen={false}
        renderOnlyChildren={hasSinglePromotion}
        trigger={({ open }) => (
          <div
            aria-label="toggle promotion collapsible"
            className="flex w-full cursor-pointer justify-between rounded-lg bg-white p-4"
          >
            <div className="flex flex-row items-center gap-2">
              <ImageWithFallback
                img={supplierLogoUrl}
                fallback="product-badge"
                className="h-12 w-12 rounded-full"
              />
              <div className="flex flex-col">
                <H2>
                  {promotions.length}{" "}
                  {pluralize("promotion", promotions.length)}
                </H2>
                <H6>
                  {promotions.map((promotion) => promotion.name).join(" • ")}
                </H6>
              </div>
            </div>
            <div className="flex items-center">
              <span className="flex cursor-pointer items-center gap-2">
                <span>{open ? "Hide" : "Show"}</span>{" "}
                <Icon icon={open ? "CHEVRON_UP" : "CHEVRON_DOWN"} />
              </span>
            </div>
          </div>
        )}
      >
        <div className="flex flex-col gap-8 pt-4">
          {promotions.map((promotion) => {
            const promotionRewardItems =
              sortPromotionRewardItemsBySpendThreshold({
                promotionRewardItems: promotion.PromotionRewardItems,
              });
            return (
              <div key={promotion.id} className="flex flex-col gap-6">
                <div className="flex flex-col">
                  <div className="flex items-center gap-4">
                    <H2>{promotion.name}</H2>
                    <BadgePurple icon="PARTY_POPPER">Promotion</BadgePurple>
                  </div>
                  {promotion.rewardRule === "one_of" && (
                    <H6>1 reward can be claimed</H6>
                  )}
                  {promotion.rewardRule === "all_of" && (
                    <H6>All unlocked rewards can be claimed</H6>
                  )}
                </div>
                <div className="flex flex-col divide-y divide-zinc-100 rounded-lg bg-white">
                  {promotionRewardItems.map((promotionRewardItem) => {
                    const hasPromotionRewardItemBeenClaimed =
                      claimedPromotionRewardItemIds.includes(
                        promotionRewardItem.id,
                      );

                    const isAddToBasketDisabled =
                      getPercentageSpendRequirementMet({
                        spendThreshold: promotionRewardItem.spendThreshold,
                        basketSubtotal,
                      }) !== 100;

                    return (
                      <StorefrontPromotionRewardItem
                        key={promotionRewardItem.id}
                        promotionRewardItem={promotionRewardItem}
                        basketSubtotal={basketSubtotal}
                        action={
                          hasPromotionRewardItemBeenClaimed ? (
                            <SmallButton
                              onClick={() => {
                                unclaimPromotionRewardItem({
                                  promotionRewardItemIds: [
                                    promotionRewardItem.id,
                                  ],
                                  onComplete: handleApplyPromotionsToBasket,
                                });
                              }}
                              color="slim"
                              disabled={false}
                              className="bg-zinc-200"
                            >
                              Remove
                            </SmallButton>
                          ) : (
                            <SmallButton
                              onClick={() =>
                                handleAddToBasket({
                                  promotionRewardItem,
                                })
                              }
                              color="slim"
                              disabled={isAddToBasketDisabled}
                            >
                              Add to basket
                            </SmallButton>
                          )
                        }
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </StorefrontPromotionCollapsible>
    </div>
  );
};

type PromotionType = PromotionListResponseType[number];
type PromotionListRewardItemType =
  PromotionType["PromotionRewardItems"][number];

const StorefrontPromotionRewardItem = ({
  promotionRewardItem,
  basketSubtotal,
  action,
}: {
  promotionRewardItem: PromotionListRewardItemType;
  basketSubtotal: number;
  action: ReactNode;
}) => {
  const widthGreaterThan768px = useMediaQuery("(min-width: 768px)");
  const quantityText =
    promotionRewardItem.quantity === 1 ? (
      "a"
    ) : (
      <strong>{promotionRewardItem.quantity}x</strong>
    );
  const priceText =
    promotionRewardItem.price === 0
      ? "free"
      : formatCurrency(promotionRewardItem.price);
  const productName =
    promotionRewardItem.Product.nameV2 ?? promotionRewardItem.Product.name;
  const productImage =
    promotionRewardItem.Product.imageUrl ??
    promotionRewardItem.Product.Parent?.imageUrl;

  const percentageSpendRequirementMet = getPercentageSpendRequirementMet({
    spendThreshold: promotionRewardItem.spendThreshold,
    basketSubtotal,
  });

  const leftToSpend = Math.max(
    0,
    promotionRewardItem.spendThreshold - basketSubtotal,
  );

  const percentageProgressBar = (
    <div className="flex w-full items-center gap-4">
      <div className="flex flex-1">
        <PromotionProgressBar percentage={percentageSpendRequirementMet} />
      </div>
      <div className="flex w-20">
        {percentageSpendRequirementMet === 100 ? (
          <div className="items-center justify-center rounded-full border border-purple-800 px-6 py-1">
            <Icon icon="CHECK_CIRCLE" className="text-purple-800" />
          </div>
        ) : (
          <span>{formatCurrency(leftToSpend)}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col items-center justify-between gap-4 p-4 md:flex-row">
      <div className="flex w-full items-center gap-4 md:w-5/6">
        <ImageWithFallback
          img={productImage}
          fallback="product-badge"
          className="h-8 w-8 rounded-full"
        />
        <div className="flex flex-1 flex-col gap-2">
          <p>
            Spend{" "}
            <strong>
              {formatCurrency(promotionRewardItem.spendThreshold)}
            </strong>{" "}
            and receive{" "}
            <strong>
              {quantityText} {productName}
            </strong>{" "}
            for <strong>{priceText}</strong>
          </p>
          {widthGreaterThan768px && percentageProgressBar}
        </div>
      </div>
      {!widthGreaterThan768px && percentageProgressBar}
      {action}
    </div>
  );
};

const PromotionProgressBar = ({ percentage }: { percentage: number }) => {
  return (
    <div className="relative h-3 w-full rounded-full bg-[#E8CCFC] transition-all">
      <div
        style={{
          width: `${percentage}%`,
        }}
        className="absolute h-3 rounded-full bg-purple-800 transition-all"
      />
    </div>
  );
};
